import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import MarkdownText from '../components/MarkdownText';
import api from '../api/client';

const BASE_PROG_SERIES_TAG = 'osnovy-prog-zadachi';
const SKIP_KONSPEKT_TAGS = new Set(['srezy', 'soobshcheniya-ob-oshibkah']);

function sortItems(items, mode) {
  const copy = [...(items || [])];
  if (mode === 'exam') {
    copy.sort((a, b) => {
      const ae = a.exam_number;
      const be = b.exam_number;
      if (ae == null && be == null) {
        return (a.id ?? 0) - (b.id ?? 0);
      }
      if (ae == null) return 1;
      if (be == null) return -1;
      if (ae !== be) return ae - be;
      return (a.id ?? 0) - (b.id ?? 0);
    });
  } else {
    copy.sort((a, b) => {
      const so = (a.study_order ?? 0) - (b.study_order ?? 0);
      if (so !== 0) return so;
      return (a.id ?? 0) - (b.id ?? 0);
    });
  }
  return copy;
}

function konspektSlugForTheme(themeTagSlug) {
  if (!themeTagSlug || SKIP_KONSPEKT_TAGS.has(themeTagSlug)) return null;
  return `osnovy-prog-${themeTagSlug}`;
}

export default function TopicProgramPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortMode, setSortMode] = useState('study');
  const [expandedId, setExpandedId] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState({});

  const isBaseProg = slug === 'base_prog_for_ege';

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/tasks/topic-programs/${encodeURIComponent(slug)}/`);
      setProgram(res.data);
    } catch (e) {
      setProgram(null);
      setError(
        e.response?.status === 404
          ? 'Программа не найдена.'
          : 'Не удалось загрузить программу.'
      );
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  const sortedItems = useMemo(
    () => sortItems(program?.items, sortMode),
    [program?.items, sortMode]
  );

  function goCatalog(themeTagSlug, typeSlugs) {
    const p = new URLSearchParams();
    p.append('tag', themeTagSlug);
    for (const s of typeSlugs || []) {
      if (s) p.append('tag_any', s);
    }
    navigate(`/catalog?${p.toString()}`);
  }

  function toggleType(itemId, typeTagSlug, checked) {
    setSelectedTypes((prev) => {
      const key = String(itemId);
      const set = new Set(prev[key] || []);
      if (checked) set.add(typeTagSlug);
      else set.delete(typeTagSlug);
      return { ...prev, [key]: Array.from(set) };
    });
  }

  if (loading) {
    return <p className="text-secondary">Загрузка...</p>;
  }

  if (error) {
    return <div className="alert alert-error">{error}</div>;
  }

  if (!program) {
    return null;
  }

  return (
    <div className="topic-program-page">
      <div className="page-header">
        <h1>{program.title}</h1>
        {program.description ? (
          <div className="page-subtitle markdown-inline">
            <MarkdownText text={program.description} />
          </div>
        ) : null}
        {isBaseProg ? (
          <p className="page-subtitle">
            Удобнее решать по конспектам типов:{' '}
            <Link to={`/konspekty?tag=${BASE_PROG_SERIES_TAG}`}>Основы · задачи по типам</Link>
          </p>
        ) : null}
      </div>

      <div className="topic-program-sort" role="group" aria-label="Порядок тем">
        <span className="text-secondary" style={{ marginRight: '0.75rem' }}>
          Сортировка:
        </span>
        <button
          type="button"
          className={`btn btn-outline btn-sm ${sortMode === 'study' ? 'active' : ''}`}
          onClick={() => setSortMode('study')}
        >
          Порядок изучения
        </button>
        <button
          type="button"
          className={`btn btn-outline btn-sm ${sortMode === 'exam' ? 'active' : ''}`}
          onClick={() => setSortMode('exam')}
        >
          Номер в экзамене
        </button>
      </div>

      <ul className="topic-program-list">
        {sortedItems.map((item) => {
          const themeSlug = item.tag?.slug;
          const isOpen = expandedId === item.id;
          const types = item.types || [];
          const picked = selectedTypes[String(item.id)] || [];
          const konspektSlug = isBaseProg ? konspektSlugForTheme(themeSlug) : null;

          return (
            <li key={item.id} className="topic-program-item">
              <div className="topic-program-item-head">
                <div>
                  {item.exam_number != null && (
                    <span className="badge" style={{ marginRight: '0.5rem' }}>
                      № {item.exam_number}
                    </span>
                  )}
                  <strong>{item.title}</strong>
                  {item.tag?.name && (
                    <span className="text-secondary" style={{ marginLeft: '0.35rem', fontSize: '0.9rem' }}>
                      ({item.tag.name})
                    </span>
                  )}
                </div>
                <div className="topic-program-item-actions">
                  {konspektSlug ? (
                    <Link className="btn btn-outline btn-sm" to={`/konspekty/${konspektSlug}`}>
                      Конспект
                    </Link>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={!themeSlug}
                    onClick={() => goCatalog(themeSlug, [])}
                  >
                    Все задачи темы
                  </button>
                  {types.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() => setExpandedId(isOpen ? null : item.id)}
                    >
                      {isOpen ? 'Свернуть типы' : 'Типы задач'}
                    </button>
                  )}
                </div>
              </div>
              {item.description ? (
                <div className="topic-program-item-desc markdown-inline">
                  <MarkdownText text={item.description} />
                </div>
              ) : null}

              {isOpen && types.length > 0 && (
                <div className="topic-program-types">
                  <p className="text-secondary" style={{ marginBottom: '0.5rem' }}>
                    Отметьте типы и откройте каталог (логика: тема и хотя бы один из выбранных типов).
                  </p>
                  <ul className="topic-program-type-checkboxes">
                    {types.map((t) => {
                      const slugT = t.tag?.slug;
                      const id = `type-${item.id}-${t.id}`;
                      return (
                        <li key={t.id}>
                          <label htmlFor={id}>
                            <input
                              id={id}
                              type="checkbox"
                              checked={picked.includes(slugT)}
                              disabled={!slugT}
                              onChange={(e) => toggleType(item.id, slugT, e.target.checked)}
                            />
                            {t.label || t.tag?.name || slugT}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    disabled={!themeSlug}
                    onClick={() => goCatalog(themeSlug, picked)}
                  >
                    Показать в каталоге
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {sortedItems.length === 0 && (
        <p className="text-secondary">В программе пока нет тем.</p>
      )}
    </div>
  );
}
